import React, { useState, useEffect } from "react";
import { View, StyleSheet, TextInput, ScrollView } from "react-native";
import { useRouter } from "expo-router";
import { CreateHeader } from "@/components/screens/shared";
import { ThemedText } from "@/components/ThemedText";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ProjectInput } from "@/components/inputs/ProjectInput";
import { ShiftInput } from "@/components/inputs/ShiftInput";
import { useTheme } from "@/hooks/useTheme";
import useToast from "@/hooks/useToast";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import { supabase } from "@/lib/supabase-client";
import Head from "expo-router/head";
import { projectService } from "@/services/projectService";
import { ProjectType } from "@/types/db/projects";

interface BillingItemFormData {
  client_id: string | null;
  project_id: string | null;
  shift_id: string | null;
  description: string;
  rate: string;
  quantity: string;
  unit: string;
}

export default function NewBillingItemPage() {
  const toast = useToast();
  const router = useRouter();
  const { colors } = useTheme();
  const { triggerRefresh } = useRowSelectionContext();

  const [formData, setFormData] = useState<BillingItemFormData>({
    client_id: null,
    project_id: null,
    shift_id: null,
    description: "",
    rate: "",
    quantity: "",
    unit: "hr",
  });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectType | null>(null);

  // Fetch project details when project_id changes to auto-set client
  useEffect(() => {
    let cancelled = false;
    const loadProject = async () => {
      if (!formData.project_id) {
        setSelectedProject(null);
        return;
      }
      try {
        const project = await projectService.getProjectById(formData.project_id);
        if (cancelled) return;
        setSelectedProject(project);
        // Auto-set client from project if not already set
        if (project?.client_id && !formData.client_id) {
          setFormData((prev) => ({ ...prev, client_id: project.client_id! }));
        }
      } catch (err) {
        console.error("Failed to load project:", err);
      }
    };
    loadProject();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.project_id]);

  const updateField = <K extends keyof BillingItemFormData>(key: K, value: BillingItemFormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!formData.project_id) {
      toast.showToast({ title: "Project is required", toastType: "error" });
      return;
    }

    setSaving(true);
    try {
      const rateNum = parseFloat(formData.rate.replace(/[^0-9.]/g, "")) || 0;
      const qtyNum = parseFloat(formData.quantity) || 0;

      const billingItemData = {
        client_id: formData.client_id,
        meta_data: {
          description: formData.description || null,
          rate: formData.rate ? `$${rateNum.toFixed(2)}/${formData.unit}` : null,
          quantity: qtyNum || null,
          unit: formData.unit || null,
          amount: rateNum * qtyNum,
          ...(formData.project_id && { project_id: formData.project_id }),
          ...(formData.shift_id && { shift_id: formData.shift_id }),
        },
      };

      const { error } = await supabase.from("billing_items").insert([billingItemData]);

      if (error) throw error;

      toast.showToast({ title: "Billing item created", toastType: "success" });
      triggerRefresh("billing_item");
      router.setParams({ new: undefined, panel: undefined });
    } catch (err: any) {
      toast.showToast({
        title: "Failed to create billing item",
        message: err.message,
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      client_id: null,
      project_id: null,
      shift_id: null,
      description: "",
      rate: "",
      quantity: "",
      unit: "hr",
    });
    setSelectedProject(null);
    setDirty(false);
  };

  return (
    <>
      <Head>
        <title>NEXUS New Billing Item</title>
        <meta name="description" content="" />
      </Head>
      <View style={{ flex: 1 }}>
        <CreateHeader
          entityType="billing_item"
          dirty={dirty}
          saving={saving}
          onSave={handleSave}
          onCancel={handleCancel}
          subtitle={selectedProject?.name}
        />
        <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.formContainer}>
          <View style={styles.section}>
            <ClientInput
              label="Client"
              value={formData.client_id}
              placeholder="Filter by client (optional)"
              onChange={(id) => {
                setFormData((prev) => ({ ...prev, client_id: id, project_id: null }));
                setSelectedProject(null);
                setDirty(true);
              }}
            />
          </View>

          <View style={styles.section}>
            <ProjectInput
              label="Project *"
              value={formData.project_id}
              clientId={formData.client_id}
              placeholder="Select a project"
              onChange={(id) => {
                setFormData((prev) => ({ ...prev, project_id: id, shift_id: null }));
                setDirty(true);
              }}
            />
          </View>

          <View style={styles.section}>
            <ShiftInput
              label="Shift"
              value={formData.shift_id}
              projectId={formData.project_id}
              placeholder="Select a shift (optional)"
              onChange={(id) => updateField("shift_id", id)}
            />
          </View>

          <View style={styles.section}>
            <ThemedText type="defaultSemiBold" style={styles.label}>
              Description
            </ThemedText>
            <TextInput
              style={[
                styles.input,
                styles.multilineInput,
                { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
              ]}
              value={formData.description}
              onChangeText={(v) => updateField("description", v)}
              placeholder="Enter description"
              placeholderTextColor={colors.placeholder}
              multiline
            />
          </View>

          <View style={styles.row}>
            <View style={[styles.section, { flex: 1 }]}>
              <ThemedText type="defaultSemiBold" style={styles.label}>
                Rate
              </ThemedText>
              <TextInput
                style={[
                  styles.input,
                  { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                ]}
                value={formData.rate}
                onChangeText={(v) => updateField("rate", v)}
                placeholder="e.g. 35.00"
                placeholderTextColor={colors.placeholder}
                keyboardType="decimal-pad"
              />
            </View>

            <View style={[styles.section, { flex: 1 }]}>
              <ThemedText type="defaultSemiBold" style={styles.label}>
                Quantity
              </ThemedText>
              <TextInput
                style={[
                  styles.input,
                  { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                ]}
                value={formData.quantity}
                onChangeText={(v) => updateField("quantity", v)}
                placeholder="e.g. 8"
                placeholderTextColor={colors.placeholder}
                keyboardType="decimal-pad"
              />
            </View>

            <View style={[styles.section, { flex: 1 }]}>
              <ThemedText type="defaultSemiBold" style={styles.label}>
                Unit
              </ThemedText>
              <TextInput
                style={[
                  styles.input,
                  { borderColor: colors.border, color: colors.text, backgroundColor: colors.backgroundSecondary },
                ]}
                value={formData.unit}
                onChangeText={(v) => updateField("unit", v)}
                placeholder="hr, day, etc."
                placeholderTextColor={colors.placeholder}
              />
            </View>
          </View>
        </ScrollView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    padding: 16,
    gap: 16,
  },
  section: {
    gap: 6,
  },
  label: {
    fontSize: 13,
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  multilineInput: {
    height: 80,
    paddingTop: 10,
    textAlignVertical: "top",
  },
  row: {
    flexDirection: "row",
    gap: 12,
  },
});
